import { formatMoney } from "@sylviq/ui-core";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { destinationSearch } from "@/shell/destination-search";
import {
  createReminderRule,
  getReminderQueue,
  listReminderRules,
  previewReminderSend,
  previewReminders,
  type ReminderCandidate,
  type ReminderRule,
  type ReminderSendPreview,
  type ReminderSendResult,
  type ReminderSpec,
  sendReminders,
} from "@/lib/api";

// Dunning reminders: stored rules evaluated minus the sent log form the
// queue (`GET .../reminders/queue`); delivery (`POST .../reminders/send`)
// goes through the mail provider (SMTP when configured, log provider
// otherwise). Nothing on this page sends without an explicit dry-run
// preview and a confirmation dialog naming the recipient count.

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const DOCUMENT_TYPES = ["invoice", "bill"] as const;
const TRIGGERS = ["before_due", "on_due", "after_due"] as const;
const RECIPIENTS = ["contact_email", "contact_persons", "custom"] as const;

const DOCUMENT_TYPE_LABELS: Record<(typeof DOCUMENT_TYPES)[number], string> = {
  invoice: "Customer invoice",
  bill: "Supplier bill",
};

const TRIGGER_LABELS: Record<(typeof TRIGGERS)[number], string> = {
  before_due: "before the due date",
  on_due: "on the due date",
  after_due: "after the due date",
};

const RECIPIENT_LABELS: Record<(typeof RECIPIENTS)[number], string> = {
  contact_email: "Contact email",
  contact_persons: "Contact people",
  custom: "Custom emails",
};

const RECIPIENT_SOURCE_LABELS: Record<string, string> = {
  contact_email: "the contact's billing email",
  contact_persons: "the contact's people",
  custom_emails: "custom emails",
  crm_lookup_deferred: "CRM contact — address looked up at send time",
};

const PROVIDER_LABELS: Record<string, string> = {
  smtp: "your mail server",
  bridge: "the Cloudflare email bridge",
  log: "the log provider (SMTP is not configured, so nothing was delivered)",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+$/;

function documentTypeLabel(value: string): string {
  return (
    DOCUMENT_TYPE_LABELS[value as (typeof DOCUMENT_TYPES)[number]] ?? value
  );
}

function triggerLabel(value: string): string {
  return TRIGGER_LABELS[value as (typeof TRIGGERS)[number]] ?? value;
}

function triggerSummary(trigger: string, triggerDays: number): string {
  if (trigger === "before_due" || trigger === "after_due") {
    return `${triggerDays} day${triggerDays === 1 ? "" : "s"} ${triggerLabel(trigger)}`;
  }
  return triggerLabel(trigger);
}

function recipientLabel(value: string): string {
  return RECIPIENT_LABELS[value as (typeof RECIPIENTS)[number]] ?? value;
}

function recipientSummary(rule: ReminderRule): string {
  switch (rule.recipient) {
    case "contact_email":
      return "the contact's billing email";
    case "contact_persons":
      return "the contact's people";
    case "custom":
      return rule.custom_emails.length > 0
        ? rule.custom_emails.join(", ")
        : "the custom email addresses";
    default:
      return rule.recipient;
  }
}

function parseEmails(value: string): string[] {
  return value
    .split(",")
    .map((email) => email.trim())
    .filter((email) => email.length > 0);
}

function CandidateTable({
  candidates,
  emptyMessage,
}: {
  candidates: ReminderCandidate[];
  emptyMessage: string;
}) {
  if (candidates.length === 0) {
    return (
      <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
        {emptyMessage}
      </div>
    );
  }
  return (
    <div className="surface overflow-hidden rounded-2xl">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Document</TableHead>
            <TableHead>Recipients</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead className="text-right">Due</TableHead>
            <TableHead className="text-right">Level</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {candidates.map((candidate) => (
            <TableRow
              key={`${candidate.rule_id ?? "preview"}-${candidate.document_id}`}
            >
              <TableCell className="text-xs">
                <span className="font-mono">{candidate.document_number}</span>{" "}
                <span className="text-[var(--muted-foreground)]">
                  ({documentTypeLabel(candidate.document_type)})
                </span>
              </TableCell>
              <TableCell className="text-xs break-all">
                {candidate.recipients.length > 0
                  ? candidate.recipients.join(", ")
                  : `— (${
                      RECIPIENT_SOURCE_LABELS[candidate.recipient_source] ??
                      candidate.recipient_source
                    })`}
              </TableCell>
              <TableCell
                className="max-w-64 truncate text-xs"
                title={candidate.body}
              >
                {candidate.subject || "—"}
              </TableCell>
              <TableCell className="text-right text-xs whitespace-nowrap tabular-nums">
                {formatMoney({
                  cents: candidate.amount_due_cents,
                  currency: "USD",
                })}
                <span className="block text-[var(--muted-foreground)]">
                  {candidate.days_overdue}d overdue
                </span>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {candidate.dunning_level_after ?? "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function RemindersPage({ rulesOnly = false }: { rulesOnly?: boolean }) {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [asOf, setAsOf] = useState(todayIso);
  const [appliedAsOf, setAppliedAsOf] = useState(todayIso());
  const [queue, setQueue] = useState<ReminderCandidate[] | null>(null);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [queueSearch, setQueueSearch] = useState("");

  const [dryRun, setDryRun] = useState<ReminderSendPreview | null>(null);
  const [dryRunError, setDryRunError] = useState<string | null>(null);
  const [sendPhase, setSendPhase] = useState<"idle" | "preview" | "send">(
    "idle",
  );
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [sendResult, setSendResult] = useState<ReminderSendResult | null>(null);

  const [rules, setRules] = useState<ReminderRule[] | null>(null);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [rulesSearch, setRulesSearch] = useState("");
  const [ruleEditorOpen, setRuleEditorOpen] = useState(false);

  const [documentType, setDocumentType] =
    useState<(typeof DOCUMENT_TYPES)[number]>("invoice");
  const [trigger, setTrigger] =
    useState<(typeof TRIGGERS)[number]>("after_due");
  const [triggerDays, setTriggerDays] = useState("7");
  const [recipient, setRecipient] =
    useState<(typeof RECIPIENTS)[number]>("contact_email");
  const [customEmails, setCustomEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [ruleFormError, setRuleFormError] = useState<string | null>(null);
  const [rulePreview, setRulePreview] = useState<ReminderCandidate[] | null>(
    null,
  );
  const [rulePreviewError, setRulePreviewError] = useState<string | null>(null);

  const ruleFormBusy = previewBusy || createBusy;

  const reloadQueue = useCallback((date: string) => {
    setQueueLoading(true);
    setQueueError(null);
    getReminderQueue(date ? { as_of: date } : {})
      .then(setQueue)
      .catch((cause: unknown) => setQueueError(toErrorMessage(cause)))
      .finally(() => setQueueLoading(false));
  }, []);

  const reloadRules = useCallback(() => {
    setRulesLoading(true);
    setRulesError(null);
    listReminderRules()
      .then(setRules)
      .catch((cause: unknown) => setRulesError(toErrorMessage(cause)))
      .finally(() => setRulesLoading(false));
  }, []);

  useEffect(() => {
    if (!rulesOnly) reloadQueue(appliedAsOf);
  }, [appliedAsOf, reloadQueue, rulesOnly]);

  useEffect(reloadRules, [reloadRules]);

  const submitQueueDate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDryRun(null);
    setDryRunError(null);
    setAppliedAsOf(asOf.trim());
  };

  const submitDryRun = async () => {
    setSendPhase("preview");
    setDryRunError(null);
    setNotice(null);
    setSendResult(null);
    try {
      const result = await previewReminderSend(
        appliedAsOf ? { as_of: appliedAsOf } : {},
      );
      setDryRun(result);
    } catch (cause) {
      setDryRun(null);
      setDryRunError(toErrorMessage(cause));
    } finally {
      setSendPhase("idle");
    }
  };

  const openSendConfirm = () => {
    if (dryRun === null) return;
    setConfirmSendOpen(true);
  };

  const performSend = async () => {
    setConfirmSendOpen(false);
    setSendPhase("send");
    setError(null);
    setNotice(null);
    try {
      const result = await sendReminders(
        appliedAsOf ? { as_of: appliedAsOf } : {},
      );
      setSendResult(result);
      setDryRun(null);
      const via = PROVIDER_LABELS[result.provider] ?? result.provider;
      setNotice(
        `${result.sent} of ${result.attempted} reminder(s) sent via ${via}` +
          (result.failed > 0 ? `; ${result.failed} failed.` : "."),
      );
      if (!rulesOnly) reloadQueue(appliedAsOf);
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSendPhase("idle");
    }
  };

  const specFromForm = (): ReminderSpec => ({
    document_type: documentType,
    trigger,
    ...(triggerDays.trim() ? { trigger_days: Number(triggerDays) } : {}),
    recipient,
    ...(customEmails.trim()
      ? { custom_emails: parseEmails(customEmails) }
      : {}),
    ...(subject.trim() ? { subject_template: subject.trim() } : {}),
    ...(body.trim() ? { body_template: body.trim() } : {}),
  });

  const validateRuleForm = (): string | null => {
    if (trigger !== "on_due") {
      const days = Number(triggerDays);
      if (triggerDays.trim() === "" || !Number.isInteger(days) || days < 0) {
        return "Trigger days must be a whole number of days (0 or more).";
      }
    }
    if (recipient === "custom") {
      const emails = parseEmails(customEmails);
      if (emails.length === 0) {
        return "Custom recipients need at least one email address.";
      }
      if (emails.some((email) => !EMAIL_PATTERN.test(email))) {
        return "Every custom recipient must look like an email address (name@example.com).";
      }
    }
    return null;
  };

  const submitRulePreview = async () => {
    const validation = validateRuleForm();
    setRuleFormError(validation);
    if (validation) return;
    setPreviewBusy(true);
    setRulePreviewError(null);
    setError(null);
    try {
      const candidates = await previewReminders({
        ...specFromForm(),
        ...(appliedAsOf ? { as_of: appliedAsOf } : {}),
      });
      setRulePreview(candidates);
    } catch (cause) {
      setRulePreview(null);
      setRulePreviewError(toErrorMessage(cause));
    } finally {
      setPreviewBusy(false);
    }
  };

  const submitRule = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validation = validateRuleForm();
    setRuleFormError(validation);
    if (validation) return;
    setCreateBusy(true);
    setError(null);
    setNotice(null);
    try {
      const created = await createReminderRule(specFromForm());
      setNotice(`Reminder rule created (${created.id}).`);
      setRulePreview(null);
      setRulePreviewError(null);
      setRuleEditorOpen(false);
      reloadRules();
      if (!rulesOnly) reloadQueue(appliedAsOf);
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setCreateBusy(false);
    }
  };

  const queueFilterText = queueSearch.trim().toLowerCase();
  const visibleQueue = (queue ?? []).filter((candidate) => {
    if (queueFilterText === "") return true;
    return [
      candidate.document_number,
      documentTypeLabel(candidate.document_type),
      candidate.subject,
      ...candidate.recipients,
    ].some((value) => value.toLowerCase().includes(queueFilterText));
  });

  const rulesFilterText = rulesSearch.trim().toLowerCase();
  const visibleRules = (rules ?? []).filter((rule) => {
    if (rulesFilterText === "") return true;
    return [
      documentTypeLabel(rule.document_type),
      triggerLabel(rule.trigger),
      triggerSummary(rule.trigger, rule.trigger_days),
      recipientLabel(rule.recipient),
      ...rule.custom_emails,
      rule.subject_template,
      rule.body_template,
    ].some((value) => value.toLowerCase().includes(rulesFilterText));
  });

  const asOfPhrase = appliedAsOf || "today";

  return (
    <div className="grid gap-6">
      <div>
        {rulesOnly ? (
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-semibold">Payment reminders</h2>
            <Link
              to="/reminders"
              search={(previous) => destinationSearch(previous, {})}
              className="ms-auto text-sm font-medium text-[var(--action)] underline"
            >
              Back to send queue
            </Link>
          </div>
        ) : (
          <h1 className="text-2xl font-semibold tracking-tight">Reminders</h1>
        )}
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          {rulesOnly ? (
            "Configure rules and preview the documents they match. Delivery stays on the Payment reminders page."
          ) : (
            <>
              Dunning queue — stored rules evaluated against open documents,
              minus what was already sent. Preview before sending; one
              recipient&apos;s failure never blocks the batch.
            </>
          )}
        </p>
      </div>

      {error && !ruleEditorOpen ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {!rulesOnly ? (
        <section aria-label="Send queue" className="grid gap-4">
          <h2 className="text-lg font-semibold tracking-tight">Send queue</h2>

          <form
            onSubmit={submitQueueDate}
            className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
          >
            <label
              htmlFor="rem-as-of"
              className="grid gap-1 text-xs font-medium"
            >
              As-of date (optional)
              <Input
                id="rem-as-of"
                type="date"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
                disabled={queueLoading}
              />
            </label>
            <Button type="submit" disabled={queueLoading}>
              {queueLoading ? "Running..." : "Load queue"}
            </Button>
            <span className="ms-auto flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={submitDryRun}
                disabled={sendPhase !== "idle" || queueLoading}
                title="Report the queue size without sending or logging anything"
              >
                {sendPhase === "preview"
                  ? "Running preview…"
                  : "Dry-run preview"}
              </Button>
              <Button
                type="button"
                onClick={openSendConfirm}
                disabled={
                  sendPhase !== "idle" || queueLoading || dryRun === null
                }
                title={
                  dryRun === null
                    ? "Run a dry-run preview first"
                    : `Send to ${dryRun.recipients} recipient(s)`
                }
              >
                {sendPhase === "send" ? "Sending…" : "Send reminders"}
              </Button>
            </span>
          </form>

          {sendPhase === "preview" ? (
            <p
              className="text-sm text-[var(--muted-foreground)]"
              aria-live="polite"
            >
              Running the dry-run preview — nothing is sent.
            </p>
          ) : null}

          {dryRunError ? (
            <div
              role="alert"
              className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
            >
              <p>Couldn&apos;t run the dry-run preview: {dryRunError}</p>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={submitDryRun}
                  disabled={sendPhase !== "idle"}
                >
                  Retry preview
                </Button>
              </div>
            </div>
          ) : null}

          {dryRun ? (
            <p
              className="text-sm text-[var(--muted-foreground)]"
              aria-live="polite"
            >
              {dryRun.candidates === 0
                ? `Dry run: as of ${asOfPhrase}, no open document matches the stored rules — nothing would be sent.`
                : `Dry run: ${dryRun.candidates} candidate(s) across ${dryRun.recipients} recipient(s) as of ${asOfPhrase} — nothing was sent.`}
            </p>
          ) : null}

          {sendResult && sendResult.failed > 0 ? (
            <ul className="surface grid gap-1 rounded-2xl p-5 text-sm">
              {sendResult.outcomes
                .filter((outcome) => !outcome.sent)
                .map((outcome) => (
                  <li
                    key={outcome.recipient_email}
                    className="text-[var(--danger)]"
                  >
                    {outcome.recipient_email}:{" "}
                    {outcome.error_message ?? "failed"}
                  </li>
                ))}
            </ul>
          ) : null}

          {queueError ? (
            <div
              role="alert"
              className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
            >
              <p>Couldn&apos;t load the send queue: {queueError}</p>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => reloadQueue(appliedAsOf)}
                  disabled={queueLoading}
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : queueLoading && queue === null ? (
            <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
              Loading…
            </div>
          ) : (
            <>
              {(queue ?? []).length > 0 ? (
                <label
                  htmlFor="rem-queue-search"
                  className="grid gap-1 text-xs font-medium"
                >
                  Filter queue{" "}
                  <span className="font-normal text-[var(--muted-foreground)]">
                    (document, subject or recipient)
                  </span>
                  <Input
                    id="rem-queue-search"
                    value={queueSearch}
                    onChange={(e) => setQueueSearch(e.target.value)}
                    placeholder="e.g. INV-0007"
                  />
                </label>
              ) : null}
              {visibleQueue.length === 0 && (queue ?? []).length > 0 ? (
                <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
                  No queue items match “{queueSearch.trim()}”.
                </div>
              ) : (
                <CandidateTable
                  candidates={visibleQueue}
                  emptyMessage="Queue is empty — no open documents match the stored rules (or everything was already sent)."
                />
              )}
            </>
          )}
        </section>
      ) : null}

      {rulesOnly ? (
        <section aria-label="Reminder rules" className="grid gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-semibold tracking-tight">Rules</h2>
            <Button type="button" onClick={() => setRuleEditorOpen(true)}>New reminder rule</Button>
            <span className="ms-auto">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={reloadRules}
                disabled={rulesLoading}
              >
                {rulesLoading ? "Loading..." : "Refresh"}
              </Button>
            </span>
          </div>

          {rulesError ? (
            <div
              role="alert"
              className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
            >
              <p>Couldn&apos;t load reminder rules: {rulesError}</p>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={reloadRules}
                  disabled={rulesLoading}
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : rulesLoading && rules === null ? (
            <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
              Loading…
            </div>
          ) : (rules ?? []).length === 0 ? (
            <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
              No rules yet — choose New reminder rule to create the first one.
            </div>
          ) : (
            <div className="grid gap-3">
              <label
                htmlFor="rem-rules-search"
                className="grid gap-1 text-xs font-medium"
              >
                Filter rules{" "}
                <span className="font-normal text-[var(--muted-foreground)]">
                  (document, trigger or recipient)
                </span>
                <Input
                  id="rem-rules-search"
                  value={rulesSearch}
                  onChange={(e) => setRulesSearch(e.target.value)}
                  placeholder="e.g. overdue"
                />
              </label>
              {visibleRules.length === 0 ? (
                <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
                  No rules match “{rulesSearch.trim()}”.
                </div>
              ) : (
                <div className="surface overflow-hidden rounded-2xl">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Document</TableHead>
                        <TableHead>Trigger</TableHead>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Summary</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleRules.map((rule) => (
                        <TableRow key={rule.id}>
                          <TableCell className="text-xs">
                            {documentTypeLabel(rule.document_type)}
                          </TableCell>
                          <TableCell className="text-xs">
                            {triggerSummary(rule.trigger, rule.trigger_days)}
                          </TableCell>
                          <TableCell className="text-xs">
                            {recipientLabel(rule.recipient)}
                            {rule.recipient === "custom" &&
                            rule.custom_emails.length > 0
                              ? `: ${rule.custom_emails.join(", ")}`
                              : null}
                          </TableCell>
                          <TableCell className="text-xs">
                            {rule.enabled ? "Enabled" : "Disabled"}
                          </TableCell>
                          <TableCell className="text-xs text-[var(--muted-foreground)]">
                            {`Applies to ${documentTypeLabel(
                              rule.document_type,
                            ).toLowerCase()}: ${triggerSummary(
                              rule.trigger,
                              rule.trigger_days,
                            )}, to ${recipientSummary(rule)}.`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          <Drawer
            open={ruleEditorOpen}
            onOpenChange={(next) => { if (!ruleFormBusy) setRuleEditorOpen(next); }}
            title="New reminder rule"
            description="Choose when to remind and who receives it. Preview evaluates this draft without saving or sending."
            className="w-[min(46rem,100%)]"
            footer={
              <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                <Button type="button" variant="secondary" className="col-span-2" disabled={ruleFormBusy} onClick={() => setRuleEditorOpen(false)}>Cancel</Button>
                <Button type="button" variant="secondary" onClick={submitRulePreview} disabled={ruleFormBusy} title="Evaluate this spec without storing a rule">
                  {previewBusy ? "Evaluating…" : "Preview"}
                </Button>
                <Button type="submit" form="reminder-rule-form" disabled={ruleFormBusy}>
                  {createBusy ? "Saving..." : "Create rule"}
                </Button>
              </div>
            }
          >
          {error ? <p role="alert" className="mb-3 text-sm text-[var(--danger)]">{error}</p> : null}
          <form
            id="reminder-rule-form"
            onSubmit={submitRule}
            className="grid gap-3"
          >
            <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_1fr]">
              <label
                htmlFor="rule-doc"
                className="grid gap-1 text-xs font-medium"
              >
                Document type
                <select
                  id="rule-doc"
                  className="select"
                  value={documentType}
                  onChange={(e) =>
                    setDocumentType(e.target.value as typeof documentType)
                  }
                  disabled={ruleFormBusy}
                >
                  {DOCUMENT_TYPES.map((option) => (
                    <option key={option} value={option}>
                      {DOCUMENT_TYPE_LABELS[option]}
                    </option>
                  ))}
                </select>
              </label>
              <label
                htmlFor="rule-trigger"
                className="grid gap-1 text-xs font-medium"
              >
                Trigger
                <select
                  id="rule-trigger"
                  className="select"
                  value={trigger}
                  onChange={(e) => setTrigger(e.target.value as typeof trigger)}
                  disabled={ruleFormBusy}
                >
                  {TRIGGERS.map((option) => (
                    <option key={option} value={option}>
                      {TRIGGER_LABELS[option]}
                    </option>
                  ))}
                </select>
              </label>
              <label
                htmlFor="rule-days"
                className="grid gap-1 text-xs font-medium"
              >
                Trigger days
                <Input
                  id="rule-days"
                  type="number"
                  value={triggerDays}
                  onChange={(e) => setTriggerDays(e.target.value)}
                  min="0"
                  disabled={ruleFormBusy || trigger === "on_due"}
                />
                <span className="font-normal text-[var(--muted-foreground)]">
                  {trigger === "on_due"
                    ? "Not used when the reminder goes out on the due date."
                    : "Whole days relative to the due date (0 or more)."}
                </span>
              </label>
              <label
                htmlFor="rule-recipient"
                className="grid gap-1 text-xs font-medium"
              >
                Recipient
                <select
                  id="rule-recipient"
                  className="select"
                  value={recipient}
                  onChange={(e) =>
                    setRecipient(e.target.value as typeof recipient)
                  }
                  disabled={ruleFormBusy}
                >
                  {RECIPIENTS.map((option) => (
                    <option key={option} value={option}>
                      {RECIPIENT_LABELS[option]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label
              htmlFor="rule-emails"
              className="grid gap-1 text-xs font-medium"
            >
              Custom emails{" "}
              <span className="font-normal text-[var(--muted-foreground)]">
                (used when the recipient is “Custom emails”, comma-separated)
              </span>
              <Input
                id="rule-emails"
                value={customEmails}
                onChange={(e) => setCustomEmails(e.target.value)}
                placeholder="billing@example.com, …"
                disabled={ruleFormBusy}
              />
            </label>
            <div className="grid gap-1">
              <div className="grid gap-3 sm:grid-cols-2">
                <label
                  htmlFor="rule-subject"
                  className="grid gap-1 text-xs font-medium"
                >
                  Subject template{" "}
                  <span className="font-normal">(optional)</span>
                  <Input
                    id="rule-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Payment reminder: {{documentNumber}}"
                    disabled={ruleFormBusy}
                  />
                </label>
                <label
                  htmlFor="rule-body"
                  className="grid gap-1 text-xs font-medium"
                >
                  Body template <span className="font-normal">(optional)</span>
                  <Input
                    id="rule-body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Hi {{contactName}}, invoice {{documentNumber}} for {{amountDue}} was due on {{dueDate}}."
                    disabled={ruleFormBusy}
                  />
                </label>
              </div>
              <p className="text-xs text-[var(--muted-foreground)]">
                Placeholders are replaced per document: {"{{contactName}}"}{" "}
                (customer), {"{{documentNumber}}"}, {"{{amountDue}}"},{" "}
                {"{{dueDate}}"}, {"{{organizationName}}"}{" "}
                (your organization) and {"{{daysOverdue}}"}{" "}
                (whole days past due, 0 if not yet overdue). A template left
                blank sends that part empty, so write the full subject and body
                you want.
              </p>
            </div>
            {ruleFormError ? (
              <p role="alert" className="text-xs text-[var(--danger)]">
                {ruleFormError}
              </p>
            ) : null}
          </form>

          {previewBusy ? (
            <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
              Checking this spec against the open documents…
            </div>
          ) : null}

          {rulePreviewError ? (
            <div
              role="alert"
              className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
            >
              <p>
                Couldn&apos;t preview this spec: {rulePreviewError}
              </p>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={submitRulePreview}
                  disabled={ruleFormBusy}
                >
                  Retry preview
                </Button>
              </div>
            </div>
          ) : null}

          {rulePreview !== null ? (
            <CandidateTable
              candidates={rulePreview}
              emptyMessage={`This spec matches nothing as of ${asOfPhrase}.`}
            />
          ) : null}
          </Drawer>
        </section>
      ) : (
        <Link
          to="/reminders"
          search={(previous) => destinationSearch(previous, { section: "rules" })}
        >
          Configure reminder rules
        </Link>
      )}

      {!rulesOnly ? (
        <Dialog
          open={confirmSendOpen}
          onOpenChange={setConfirmSendOpen}
          title="Send reminder emails now?"
          description="This dispatches reminder emails to every recipient in the queue and logs each send. It cannot be undone."
        >
          <div className="grid gap-4">
            <p className="text-sm">
              You are about to send{" "}
              <span className="font-semibold">
                {dryRun?.recipients ?? 0} email(s)
              </span>{" "}
              for{" "}
              <span className="font-semibold">
                {dryRun?.candidates ?? 0} document(s)
              </span>{" "}
              queued as of{" "}
              <span className="font-semibold">{asOfPhrase}</span>.
            </p>
            <p className="text-xs text-[var(--muted-foreground)]">
              Delivery uses the configured mail provider. Reminders already
              sent for a rule and document are not sent again. Run the dry-run
              preview first if you want to check the queue before confirming.
            </p>
            <div className="actions-row">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirmSendOpen(false)}
              >
                Cancel
              </Button>
              <Button type="button" onClick={() => void performSend()}>
                Send now
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
